import React, { useCallback, useEffect, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { contourVersionAtom, nnSegmentationResultAtom } from "../../atoms/segmentationAtoms";
import { SegmentationViewer3D, hullVolumeMm3Atom, hullAxesAtom } from "./SegmentationViewer3D";
import { SegmentationStatsPanel } from "./SegmentationStatsPanel";
import { getCornerstone, getCornerstoneTools } from "../../utils/dicomLoader";
import { refreshViewerFromAnnotations } from "../../tools/nnInteractiveSession";
import { PLANE_COLORS_HEX } from "../../constants/planeColors";
import { isLoadedFromMask } from "../../utils/annotationFlags";

/**
 * Composite panel: full-size 3D viewer with stats overlaid in the top-right.
 * Falls back to full stats panel when no multi-plane data is available.
 * Listens for Cornerstone annotation removal events to refresh 3D data.
 */
export const SegmentationViewerPanel: React.FC = () => {
  const segResult = useAtomValue(nnSegmentationResultAtom);
  const setNnSegResult = useSetAtom(nnSegmentationResultAtom);
  // Show the 3D viewer on any contour data — even a single axial slice
  // renders as an informative flat ring at its true z. The previous gate
  // (axial ≥ 2 slices OR axial ≥ 1 + companions) avoided a degenerate
  // single-ring projection caused by the canonicalize sidecar bug; that
  // bug was fixed bucket-wide (dev-data-processing#224 + sweep) and the
  // protective 423 gate was removed in web-demo-backend#313. A single
  // ring beats the empty stats panel — the user sees the contour they
  // just produced.
  const axialSliceCount = segResult ? Object.keys(segResult.contours).length : 0;
  const hasCompanionSlices = (() => {
    const pc = segResult?.planeContours;
    if (!pc) return false;
    return (
      (pc.coronal && Object.keys(pc.coronal.contours).length > 0) ||
      (pc.sagittal && Object.keys(pc.sagittal.contours).length > 0)
    );
  })();

  // Also mount the 3D viewer when any PlanarFreehand contour annotations
  // exist — lasso/bump strokes never populate nnSegmentationResultAtom, so
  // gating on segResult alone hid the 3D viewer for the entire lasso flow.
  //
  // Driven off Cornerstone's annotation events (not contourVersionAtom)
  // because mask-loaded annotations (niftiMaskLoader) don't bump
  // contourVersion — a useMemo gated on contourVersion would cache `false`
  // at mount and never re-evaluate when the saved masks land.
  const recomputeHasContours = useCallback(() => {
    try {
      const csTools = getCornerstoneTools();
      const anns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
      // Exclude `_loadedFromMask` annotations — those are saved masks that
      // niftiMaskLoader rehydrates on case open. Counting them would mount the
      // 3D viewer immediately on case open, before the user has drawn anything.
      // Saved masks belong on the 2D viewports; the 3D viewer is reserved for
      // the in-progress annotation.
      return anns.some(
        (a) =>
          a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
          (a?.data?.contour?.polyline?.length ?? 0) >= 3 &&
          !isLoadedFromMask(a),
      );
    } catch {
      return false;
    }
  }, []);

  const [hasContourAnnotations, setHasContourAnnotations] = useState<boolean>(() =>
    recomputeHasContours(),
  );

  // Re-check on the events that materially change which contour annotations
  // exist. ANNOTATION_ADDED covers fresh lasso strokes AND niftiMaskLoader
  // restores; ANNOTATION_REMOVED + contourVersion bump (on save/delete) cover
  // teardown.
  const contourVersion = useAtomValue(contourVersionAtom);
  useEffect(() => {
    let target: any = null;
    try {
      target = getCornerstone().eventTarget;
    } catch {
      return;
    }
    if (!target) return;
    const update = () => setHasContourAnnotations(recomputeHasContours());
    update();
    const events = [
      "CORNERSTONE_TOOLS_ANNOTATION_ADDED",
      "CORNERSTONE_TOOLS_ANNOTATION_REMOVED",
      "CORNERSTONE_TOOLS_ANNOTATION_COMPLETED",
    ];
    for (const evt of events) target.addEventListener(evt, update);
    return () => {
      for (const evt of events) target.removeEventListener(evt, update);
    };
  }, [recomputeHasContours, contourVersion]);

  const has3DData =
    (segResult != null && (axialSliceCount >= 1 || hasCompanionSlices)) || hasContourAnnotations;

  // Refresh 3D viewer when annotations are removed outside the nnInteractive flow
  useEffect(() => {
    if (!has3DData) return;
    let target: any = null;
    try {
      target = getCornerstone().eventTarget;
    } catch {
      return;
    }
    if (!target) return;

    const onRemoved = () => refreshViewerFromAnnotations();
    target.addEventListener("CORNERSTONE_TOOLS_ANNOTATION_REMOVED", onRemoved);
    return () => {
      target.removeEventListener("CORNERSTONE_TOOLS_ANNOTATION_REMOVED", onRemoved);
    };
  }, [has3DData, setNnSegResult]);

  if (!has3DData) {
    return <SegmentationStatsPanel />;
  }

  return (
    <div className="w-full h-full relative bg-bg-2">
      <SegmentationViewer3D />
      <StatsOverlay />
      <PlaneLegend />
    </div>
  );
};

/** Color key showing which contour-ring color corresponds to which plane.
 *  Pinned bottom-left so it doesn't fight with the stats overlay (top-right). */
const PlaneLegend: React.FC = () => (
  <div className="absolute pointer-events-none select-none z-10" style={{ left: 8, bottom: 8 }}>
    <div
      className="text-[11px] font-mono leading-relaxed"
      style={{
        textShadow: "0 1px 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.6)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        borderRadius: 4,
        padding: "4px 6px",
        background: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <LegendRow color={PLANE_COLORS_HEX.axial} label="Axial" />
      <LegendRow color={PLANE_COLORS_HEX.coronal} label="Coronal" />
      <LegendRow color={PLANE_COLORS_HEX.sagittal} label="Sagittal" />
    </div>
  </div>
);

const LegendRow: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 2,
        background: color,
        borderRadius: 1,
      }}
    />
    <span style={{ color }}>{label}</span>
  </div>
);

/** Stats overlay matching SegmentationStatsOverlay box style from 2D viewports. */
const StatsOverlay: React.FC = () => {
  const hullVolume = useAtomValue(hullVolumeMm3Atom);
  const hullAxes = useAtomValue(hullAxesAtom);
  if (hullVolume == null) return null;

  return (
    <div className="absolute inset-0 pointer-events-none select-none z-10">
      <div className="absolute" style={{ top: 8, right: 8 }}>
        <div
          className="text-[11px] font-mono leading-relaxed"
          style={{
            color: "#93b4ff",
            textShadow: "0 1px 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.6)",
            border: "1px solid rgba(67, 97, 238, 0.4)",
            borderRadius: 4,
            padding: "4px 6px",
            background: "rgba(0, 0, 0, 0.5)",
          }}
        >
          <div>
            Vol:{" "}
            {hullVolume >= 1000
              ? `${(hullVolume / 1000).toFixed(1)} cm³`
              : `${hullVolume.toFixed(0)} mm³`}
          </div>
          {hullAxes?.map((axis, i) => (
            <div key={i} style={{ color: axis.color }}>
              <span style={{ fontWeight: 700 }}>{axis.length.toFixed(1)}</span> mm
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
